import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  Hash,
} from 'lucide-react';
import clsx from 'clsx';
import {
  ancestorIds,
  nodeToJson,
  type JsonNode,
  type JsonValueType,
} from '../lib/jsonTree';

/**
 * Flat, json.cn-style tree renderer. The tree is "lined up" by
 * inserting open / close bracket rows so a 3-level object visually
 * matches what users see in their editor:
 *
 *   ▼ "user": {
 *       "name": "Alice"
 *       "age": 30
 *     }
 *
 * Each row (leaf or bracket) carries its own indent + hover affordance
 * for "复制路径" / "复制值". The flattening is recomputed on every
 * change to `expanded`; it's O(visible nodes) and the tree we render
 * is bounded by what the user has asked to see.
 */
export interface JsonTreeViewProps {
  root: JsonNode;
  /** Search hit ids (lit up with a soft highlight). */
  highlightIds?: ReadonlySet<number>;
  /** Currently-active hit (lit up brighter + scrolled into view). */
  activeId?: number;
  /**
   * One-shot expand / collapse command from the parent. The component
   * watches `tick` (not `ids`); each new tick value replaces the
   * internal `expanded` set with `new Set(ids)`. Use null to express
   * "no command yet". Why not a controlled `expanded` prop: users also
   * toggle individual rows directly, and lifting that state to the
   * parent forces every click through React state shared with the
   * page, which is slower and noisier than necessary.
   */
  expandCommand?: { tick: number; ids: ReadonlySet<number> } | null;
  /** Callback whenever a node is selected (to drive the path bar). */
  onSelect?: (node: JsonNode) => void;
  /** Plain-text query used to inline-highlight matches inside key / value. */
  query?: string;
}

export type FlatRow =
  | { kind: 'leaf'; node: JsonNode; isLast: boolean }
  | { kind: 'open'; node: JsonNode; collapsed: boolean; isLast: boolean }
  | { kind: 'close'; node: JsonNode; isLast: boolean };

const INDENT_PX = 16;
/**
 * Auto-expand heuristic: containers with <= this many children open by
 * default. Bigger containers stay collapsed so the user isn't dropped
 * into a 10k-line wall.
 */
const AUTO_EXPAND_LIMIT = 24;

export const JsonTreeView = forwardRef<HTMLDivElement, JsonTreeViewProps>(
  function JsonTreeView(
    { root, highlightIds, activeId, expandCommand, onSelect, query },
    ref,
  ) {
    const [expanded, setExpanded] = useState<Set<number>>(() => seedExpanded(root));

    // Re-seed when a brand-new root arrives (the user pasted different
    // JSON). Comparing by `root.id` is sufficient because `buildTree`
    // assigns ids deterministically per-tree, so a re-render with the
    // same parsed value gives the same root id.
    const lastRootIdRef = useRef<number>(root.id);
    useEffect(() => {
      if (lastRootIdRef.current === root.id) return;
      lastRootIdRef.current = root.id;
      setExpanded(seedExpanded(root));
    }, [root]);

    // Apply parent-driven expand / collapse command. Watching only
    // `tick` makes it a one-shot signal: even if the parent rebuilds
    // the same `ids` Set on every render, we won't fight the user's
    // subsequent manual toggles. The very first render carries
    // `tick` (often 0); we deliberately skip applying it because a
    // null `expandCommand` means "no command yet" — parents that want
    // to force-collapse on mount must bump tick to >= 1.
    const lastTickRef = useRef<number | null>(null);
    useEffect(() => {
      if (!expandCommand) return;
      if (lastTickRef.current === expandCommand.tick) return;
      lastTickRef.current = expandCommand.tick;
      setExpanded(new Set(expandCommand.ids));
    }, [expandCommand]);

    const toggle = useCallback((id: number) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    // Keep ancestors of the active hit expanded so we can scroll to it.
    useEffect(() => {
      if (activeId === undefined) return;
      const trail = ancestorIds(root, activeId);
      if (trail.size === 0) return;
      setExpanded((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (const id of trail) {
          if (!next.has(id)) {
            next.add(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [activeId, root]);

    const flat = useMemo(() => flatten(root, expanded), [root, expanded]);

    const activeRowRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      if (activeId === undefined) return;
      const el = activeRowRef.current;
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [activeId, flat]);

    return (
      <div
        ref={ref}
        className="font-mono text-[12.5px] leading-6 select-text"
      >
        {flat.map((row, idx) => {
          const node = row.node;
          const isHit = highlightIds?.has(node.id);
          const isActive = activeId === node.id;
          return (
            <Row
              key={`${row.kind}-${node.id}-${idx}`}
              row={row}
              isHit={!!isHit}
              isActive={!!isActive}
              query={query}
              activeRowRef={isActive ? activeRowRef : undefined}
              onToggle={toggle}
              onSelect={onSelect}
            />
          );
        })}
      </div>
    );
  },
);

function Row({
  row,
  isHit,
  isActive,
  query,
  activeRowRef,
  onToggle,
  onSelect,
}: {
  row: FlatRow;
  isHit: boolean;
  isActive: boolean;
  query?: string;
  activeRowRef?: React.MutableRefObject<HTMLDivElement | null>;
  onToggle: (id: number) => void;
  onSelect?: (node: JsonNode) => void;
}): JSX.Element {
  const node = row.node;
  const indentStyle: CSSProperties = { paddingLeft: node.depth * INDENT_PX + 8 };

  const baseRow = clsx(
    'group flex items-start gap-1 px-2 hover:bg-bg-soft cursor-pointer',
    isHit && 'bg-amber-100/60 dark:bg-amber-500/10',
    isActive && 'bg-amber-300/70 dark:bg-amber-500/30 ring-1 ring-amber-400',
  );

  // Bracket-close row is a one-liner with nothing more to show.
  if (row.kind === 'close') {
    return (
      <div
        ref={activeRowRef}
        className={baseRow}
        style={indentStyle}
        onClick={() => onSelect?.(node)}
      >
        <span className="w-3 shrink-0" />
        <span className="text-ink-muted">
          {node.type === 'object' ? '}' : ']'}
          {row.isLast ? '' : ','}
        </span>
      </div>
    );
  }

  if (row.kind === 'open') {
    const Caret = row.collapsed ? ChevronRight : ChevronDown;
    return (
      <div
        ref={activeRowRef}
        className={baseRow}
        style={indentStyle}
        onClick={() => onSelect?.(node)}
      >
        <Caret
          className="mt-1 h-3 w-3 shrink-0 cursor-pointer text-ink-dim hover:text-accent"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.id);
          }}
        />
        {node.depth > 0 && (
          <KeyPart node={node} query={query} />
        )}
        <span className="text-ink-muted">{node.type === 'object' ? '{' : '['}</span>
        {row.collapsed && (
          <span
            className="text-ink-dim"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
          >
            {' '}
            …{' '}
            <span className="text-ink-dim">
              {node.size}
              {node.type === 'object' ? ' 项' : ' 个'}
            </span>{' '}
          </span>
        )}
        {row.collapsed && (
          <>
            <span className="text-ink-muted">{node.type === 'object' ? '}' : ']'}</span>
            {!row.isLast && <span className="text-ink-muted">,</span>}
          </>
        )}
        <RowActions node={node} />
      </div>
    );
  }

  // 'leaf' — primitive or empty container.
  return (
    <div
      ref={activeRowRef}
      className={baseRow}
      style={indentStyle}
      onClick={() => onSelect?.(node)}
    >
      <span className="w-3 shrink-0" />
      {node.depth > 0 && <KeyPart node={node} query={query} />}
      <ValuePart node={node} query={query} />
      {!row.isLast && <span className="text-ink-muted">,</span>}
      <RowActions node={node} />
    </div>
  );
}

function KeyPart({ node, query }: { node: JsonNode; query?: string }): JSX.Element {
  // Numeric keys render as bracket-quoted indices in the path but as
  // bare numbers here — array elements don't show `[0]:` in the
  // canonical pretty-printer because the position carries the meaning.
  const isArrayIndex = /^\d+$/.test(node.key);
  if (isArrayIndex) return <></>;
  return (
    <>
      <span className="text-sky-600 dark:text-sky-400">
        "
        <Highlight text={node.key} query={query} />
        "
      </span>
      <span className="text-ink-muted">:</span>
    </>
  );
}

function ValuePart({ node, query }: { node: JsonNode; query?: string }): JSX.Element {
  if (node.type === 'object' || node.type === 'array') {
    // Empty container renders inline like '[]' / '{}'.
    if ((node.size ?? 0) === 0) {
      return (
        <span className="text-ink-muted">
          {node.type === 'object' ? '{}' : '[]'}
        </span>
      );
    }
    // Non-empty containers should never reach here as 'leaf'.
    return <></>;
  }
  if (node.type === 'string') {
    return (
      <span className="text-rose-600 dark:text-rose-400">
        "
        <Highlight text={String(node.value)} query={query} />
        "
      </span>
    );
  }
  if (node.type === 'number') {
    return (
      <span className="text-violet-600 dark:text-violet-300">
        <Highlight text={String(node.value)} query={query} />
      </span>
    );
  }
  if (node.type === 'boolean') {
    return (
      <span className="text-emerald-600 dark:text-emerald-400">
        <Highlight text={String(node.value)} query={query} />
      </span>
    );
  }
  // null
  return (
    <span className="text-ink-dim italic">
      <Highlight text="null" query={query} />
    </span>
  );
}

function Highlight({ text, query }: { text: string; query?: string }): JSX.Element {
  if (!query || query.length === 0 || !text) return <>{text}</>;
  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i === -1) return <>{text}</>;
  const before = text.slice(0, i);
  const match = text.slice(i, i + query.length);
  const after = text.slice(i + query.length);
  return (
    <>
      {before}
      <mark className="rounded bg-amber-300/60 px-0.5 dark:bg-amber-400/40">
        {match}
      </mark>
      {after}
    </>
  );
}

function RowActions({ node }: { node: JsonNode }): JSX.Element {
  return (
    <span
      className="ml-auto flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100"
      onClick={(e) => e.stopPropagation()}
    >
      <CopyPathButton node={node} />
      <CopyValueButton node={node} />
    </span>
  );
}

function CopyPathButton({ node }: { node: JsonNode }): JSX.Element {
  return (
    <CopyButton
      title="复制路径"
      icon={<Hash className="h-3 w-3" />}
      value={node.path}
    />
  );
}

function CopyValueButton({ node }: { node: JsonNode }): JSX.Element {
  return (
    <CopyButton
      title="复制值"
      icon={<ClipboardCopy className="h-3 w-3" />}
      value={nodeToJson(node, 2)}
    />
  );
}

function CopyButton({
  title,
  icon,
  value,
}: {
  title: string;
  icon: React.ReactNode;
  value: string;
}): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded p-0.5 text-ink-dim hover:bg-bg-soft hover:text-accent"
      title={title}
    >
      {done ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : icon}
    </button>
  );
}

/**
 * Flatten a tree to display rows according to which container ids are
 * `expanded`. Exported solely so the page-level "全部展开 / 全部收起"
 * button behaviour can be unit-tested without mounting Monaco / RTL —
 * see tests/web/jsonTreeTool.test.ts.
 */
export function flatten(root: JsonNode, expanded: ReadonlySet<number>): FlatRow[] {
  const out: FlatRow[] = [];
  const walk = (n: JsonNode, isLast: boolean): void => {
    if (n.type === 'object' || n.type === 'array') {
      const size = n.size ?? 0;
      if (size === 0) {
        out.push({ kind: 'leaf', node: n, isLast });
        return;
      }
      const isExpanded = expanded.has(n.id);
      out.push({
        kind: 'open',
        node: n,
        collapsed: !isExpanded,
        isLast,
      });
      if (isExpanded) {
        const children = n.children ?? [];
        children.forEach((c, i) => walk(c, i === children.length - 1));
        out.push({ kind: 'close', node: n, isLast });
      }
      return;
    }
    out.push({ kind: 'leaf', node: n, isLast });
  };
  walk(root, true);
  return out;
}

function seedExpanded(root: JsonNode): Set<number> {
  // Default: expand the root + every container with ≤ AUTO_EXPAND_LIMIT
  // children. Saves the user the click for the "small object" case
  // while keeping huge arrays collapsed so the page paints fast.
  const set = new Set<number>();
  const visit = (n: JsonNode): void => {
    if (n.type !== 'object' && n.type !== 'array') return;
    const size = n.size ?? 0;
    if (n.depth === 0 || size <= AUTO_EXPAND_LIMIT) set.add(n.id);
    n.children?.forEach(visit);
  };
  visit(root);
  return set;
}

/**
 * Build the set of every container id in the tree. Used by the page's
 * "全部展开" button.
 */
export function allContainerIds(root: JsonNode): Set<number> {
  const set = new Set<number>();
  const visit = (n: JsonNode): void => {
    if (n.type === 'object' || n.type === 'array') set.add(n.id);
    n.children?.forEach(visit);
  };
  visit(root);
  return set;
}
